import { PageHeader } from '@/shared/components/ui/PageHeader';
// The "Website" nav section's data side — every Lead the RBM website's forms
// have created (source=website), grouped by which form it came from
// (Lead.source_detail — see src/integrations/adapters/website.js's formType).
// The connection itself (webhook URL, credentials, auto-create toggle) lives
// in Settings → Integrations → Website; this page is what a BDM actually
// works from day to day.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Globe, ExternalLink, Inbox, Phone, Mail, MapPin } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ROUTES } from '@/routes/paths';

interface WebsiteLead {
  id: string;
  _id?: string;
  name: string;
  business_name?: string;
  phone?: string;
  email?: string;
  emails?: string[];
  niche?: string;
  location?: string;
  delivery_location?: string;
  additional_info?: string;
  source_detail?: string;
  status: string;
  created_at: string;
  lines?: Array<{ productName?: string; productCode?: string; quantity?: number }>;
}

const FORM_TYPE_META: Record<string, { label: string; color: string }> = {
  enquiry: { label: 'Enquiry', color: 'bg-info/10 text-info-text dark:bg-info dark:text-info-text' },
  distributor_application: { label: 'Distributor Application', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  document_request: { label: 'TDS/MSDS Request', color: 'bg-warning/10 text-warning-text dark:bg-warning dark:text-warning-text' },
  quote_cart: { label: 'Quote Cart', color: 'bg-success/10 text-success-text dark:bg-success dark:text-success-text' },
};

const FILTER_OPTIONS = [
  { key: '', label: 'All forms' },
  { key: 'enquiry', label: 'Enquiries' },
  { key: 'distributor_application', label: 'Distributor Applications' },
  { key: 'document_request', label: 'TDS/MSDS Requests' },
  { key: 'quote_cart', label: 'Quote Cart' },
] as const;

export function WebsiteLeadsPage() {
  const [formType, setFormType] = useState('');

  const leads = useQuery({
    queryKey: ['leads', { source: 'website', limit: 200, offset: 0 }],
    queryFn: async () => (await api.get<{ leads: WebsiteLead[]; total: number }>(
      '/api/leads?source=website&limit=200&offset=0',
    )).data,
  });

  const filtered = useMemo(() => {
    const rows = leads.data?.leads ?? [];
    return formType ? rows.filter((l) => l.source_detail === formType) : rows;
  }, [leads.data, formType]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const l of leads.data?.leads ?? []) c[l.source_detail || ''] = (c[l.source_detail || ''] || 0) + 1;
    return c;
  }, [leads.data]);

  return (
    <div className="max-w-[1580px] mx-auto space-y-4 pb-16">
      <PageHeader
        icon={Globe}
        eyebrow="Marketing"
        title="Website Leads"
        description="Every submission from the RBM website — auto-created here the moment it arrives."
        actions={
            <Link
              to={`${ROUTES.leadInbox}?provider=website`}
              className="inline-flex items-center gap-1.5 text-body-sm text-primary hover:underline"
            >
              <Inbox className="h-4 w-4" /> Raw submissions inbox <ExternalLink className="h-3.5 w-3.5" />
            </Link>
        }
      />
      <Card>
        <CardBody className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.map((opt) => {
            const count = opt.key ? (counts[opt.key] || 0) : (leads.data?.total ?? 0);
            const active = formType === opt.key;
            return (
              <button
                key={opt.key || 'all'}
                type="button"
                onClick={() => setFormType(opt.key)}
                className={`rounded-full border px-3 py-1.5 text-body-xs font-medium transition-colors ${
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-outline-variant text-on-surface-variant hover:bg-surface-container-high/40'
                }`}
              >
                {opt.label} {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
              </button>
            );
          })}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {leads.isLoading ? (
            <div className="p-4"><Skeleton className="h-64" /></div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Globe className="h-6 w-6" />}
              title="No website leads yet"
              description="Once the website is connected (Settings → Integrations → Website) and a visitor submits a form, it shows up here automatically."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-container-high/40 text-left text-body-xs uppercase tracking-wide text-on-surface-variant">
                  <tr>
                    <th className="px-4 py-2.5">Form</th>
                    <th className="px-4 py-2.5">Name / Company</th>
                    <th className="px-4 py-2.5">Contact</th>
                    <th className="px-4 py-2.5">Detail</th>
                    <th className="px-4 py-2.5">Received</th>
                    <th className="px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/60">
                  {filtered.map((lead) => {
                    const meta = FORM_TYPE_META[lead.source_detail || ''] || { label: 'Website', color: 'bg-surface-container-high text-on-surface-variant' };
                    const rowId = lead.id || lead._id;
                    return (
                      <tr
                        key={rowId}
                        className="cursor-pointer hover:bg-surface-container-high/30"
                        onClick={() => rowId && window.open(ROUTES.pipelineDetail.replace(':id', rowId), '_self')}
                      >
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-body-xs font-medium ${meta.color}`}>{meta.label}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-on-surface">{lead.name}</div>
                          {lead.business_name && <div className="text-body-xs text-on-surface-variant">{lead.business_name}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5 text-body-xs text-on-surface-variant">
                            {lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {lead.phone}</span>}
                            {(lead.email || lead.emails?.[0]) && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {lead.email || lead.emails?.[0]}</span>}
                            {lead.delivery_location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {lead.delivery_location}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          {lead.source_detail === 'quote_cart' && lead.lines?.length ? (
                            <span className="text-body-xs text-on-surface-variant">
                              {lead.lines.length} item(s): {lead.lines.slice(0, 2).map((l) => l.productName).filter(Boolean).join(', ')}
                              {lead.lines.length > 2 ? '…' : ''}
                            </span>
                          ) : (
                            <span className="truncate block text-body-xs text-on-surface-variant" title={lead.niche || lead.additional_info}>
                              {lead.niche || lead.additional_info || '—'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-body-xs text-on-surface-variant whitespace-nowrap">
                          {lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={lead.status === 'new' ? 'info' : 'default'}>{lead.status}</Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
